import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { printPayment, downloadPayment } from '../../utils/pdf';

const emptyForm = {
  amountPaid: '',
  date: new Date().toISOString().split('T')[0],
  mode: 'cash'
};

export default function PaymentEntry() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerDetails, setCustomerDetails] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch customers
  const fetchCustomers = () => {
    API.get('/customers')
      .then(r => setCustomers(r.data))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setLoading(false));
  };

  // Fetch individual customer details and their payments
  const fetchCustomerDetails = (id) => {
    if (!id) {
      setCustomerDetails(null);
      return;
    }
    API.get(`/customers/${id}`)
      .then(r => {
        setCustomerDetails(r.data);
        // Set customer payments log
        setPayments(r.data.payments || []);
      })
      .catch(() => toast.error('Failed to load customer details'));
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    fetchCustomerDetails(selectedCustomerId);
  }, [selectedCustomerId]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId) return toast.error('Please select a customer first');
    if (!form.amountPaid || isNaN(form.amountPaid) || parseFloat(form.amountPaid) <= 0) {
      return toast.error('Please enter a valid amount paid');
    }

    try {
      await API.post(`/customers/${selectedCustomerId}/payments`, form);
      toast.success('Payment recorded successfully');
      setForm(emptyForm);
      // Refresh customer list to update balances
      fetchCustomers();
      // Refresh current details
      fetchCustomerDetails(selectedCustomerId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to record payment');
    }
  };

  return (
    <Layout title="Customer Payment Entry">
      <div style={{ display: 'grid', gridTemplateColumns: customerDetails ? '1fr 1.2fr' : '1fr', gap: 24 }}>
        
        {/* Payment Entry Form */}
        <div className="card">
          <div className="card-header"><span className="card-title">Record Customer Payment</span></div>
          <div className="card-body">
            <form onSubmit={handleRecordPayment}>
              <div className="form-group">
                <label className="form-label">Customer Name *</label>
                {loading ? (
                  <div>Loading customers...</div>
                ) : (
                  <select 
                    className="form-control"
                    value={selectedCustomerId}
                    onChange={e => setSelectedCustomerId(e.target.value)}
                    required
                  >
                    <option value="">— Select Customer —</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.phone}) — Due: ₹{c.balance.toFixed(2)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {customerDetails && (
                <div style={{ background: 'var(--blue-light)', border: '1px solid #bee3f8', borderRadius: 'var(--radius-sm)', padding: '14px', marginBottom: 18, color: 'var(--navy)' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Outstanding Debt</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--red)', fontWeight: 700, margin: '4px 0' }}>
                    ₹{customerDetails.balance?.toFixed(2)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--gray-600)', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 6, marginTop: 4 }}>
                    <span>Total Billed: ₹{customerDetails.totalDue?.toFixed(2)}</span>
                    <span>Total Paid: ₹{customerDetails.totalPaid?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Amount Paid (₹) *</label>
                  <input 
                    className="form-control"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amountPaid}
                    onChange={e => setForm({ ...form, amountPaid: e.target.value })}
                    required
                    disabled={!selectedCustomerId}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Date</label>
                  <input 
                    className="form-control"
                    type="date"
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    required
                    disabled={!selectedCustomerId}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Payment Mode</label>
                <select 
                  className="form-control"
                  value={form.mode}
                  onChange={e => setForm({ ...form, mode: e.target.value })}
                  disabled={!selectedCustomerId}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI (GPay/PhonePe)</option>
                  <option value="net_banking">Net Banking</option>
                  <option value="discount">Discount / Wave Off</option>
                </select>
              </div>

              <button 
                type="submit" 
                className="btn btn-gold btn-lg" 
                style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                disabled={!selectedCustomerId || customerDetails?.balance <= 0}
              >
                💳 Record Customer Payment
              </button>
            </form>
          </div>
        </div>

        {/* Selected Customer Payment Logs */}
        {customerDetails && (
          <div className="card fade-in">
            <div className="card-header">
              <span className="card-title">Payment Logs: {customerDetails.name}</span>
            </div>
            <div className="table-wrap">
              {payments.length === 0 ? (
                <div className="empty-state" style={{ padding: 40 }}>
                  <div className="empty-icon">💳</div>
                  <div className="empty-title">No payments logged yet</div>
                  <div className="empty-text">Record a payment on the left to see it in this list.</div>
                </div>
              ) : (
                 <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Payment Mode</th>
                      <th style={{ textAlign: 'right' }}>Amount Paid (₹)</th>
                      <th style={{ textAlign: 'right', width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>{new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td>
                          <span className={`badge ${p.mode === 'cash' ? 'badge-green' : p.mode === 'card' ? 'badge-navy' : p.mode === 'discount' ? 'badge-red' : 'badge-amber'}`}>
                            {p.mode}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green)' }}>₹{p.amountPaid.toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn btn-outline btn-sm" onClick={() => downloadPayment(p, customerDetails)} title="Download Receipt">⬇️</button>
                            <button className="btn btn-primary btn-sm" onClick={() => printPayment(p, customerDetails)} title="Print Receipt">🖨️</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}
